import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

import { ResultData } from '@app/core/_common/model';
import { environment } from '@app/env-service';

import { RFQ, Quotation } from '../store/quotes.model';

import { RfqService } from './rfq.service';

@Injectable({
  providedIn: 'root',
})
export class QuotesService {
  url :string = environment.url;

  private readonly _record = new BehaviorSubject<ResultData>(<ResultData>{});
  public record$ = this._record.asObservable();

  private readonly _activeQuotation = new BehaviorSubject<Quotation>(<Quotation>{});
  public activeQuotation$ = this._activeQuotation.asObservable();

  public gettingRecords :boolean = false;

  private params :any = {
    per_page: 20,
    page: 1,
  };

  constructor(
    private http :HttpClient,
    private rfqService :RfqService,
  ) {
    // this.init();
  }

  init() {
    this._record.next(<ResultData>{});
    this.rfqService.setActive(<RFQ>{});
  }

  setActive(quotation :Quotation, getQuotations ?:boolean) {
    this._activeQuotation.next(quotation);
    if (getQuotations) {
      this.refreshList();
    }
  }

  get(params :any) {
    const rfq :RFQ = this.rfqService.getActiveRfq();
    if (!rfq || !rfq.id) {
      const kani = this;
      setTimeout(function () {
        kani.get(kani.params);
      }, 100);

      return;
    }
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/rfqs/${rfq.id}/quotations`, { params: params })
      .subscribe((resp :ResultData) => {
        resp.data.forEach((row :Quotation) => {
          row.tiered_pricing_json = JSON.parse(row.tiered_pricing_json);
          row.variants_influence_price = JSON.parse(row.variants_influence_price);
          // row.files = row.files.trim().split(",");
          // if (row.files == [""] || row.files[0] === "") {
          // 	row.files = [];
          // }
        });
        this._record.next(resp);
        this.gettingRecords = false;
      });
  }

  refreshList() {
    this._record.next(<ResultData>{});
    this.get(this.params);
  }

  getByRfqId(rfq_id :number, includeDeleted ?:boolean) {
    const params :any = {};
    if (includeDeleted) {
      params.dlt = 1;
    }

    return this.http
      .get(`${this.url}/rfqs/${rfq_id}/quotations`, { params: params })
      .pipe(
        tap((resp :ResultData) => {
          resp.data.forEach((row :Quotation) => {
            row.tiered_pricing_json = JSON.parse(row.tiered_pricing_json);
            row.variants_influence_price = JSON.parse(row.variants_influence_price);
          });

          return resp;
        }),
      );
  }

  getById(id :number) :Observable<Quotation> {
    return this.http.get<Quotation>(`${this.url}/quotations/${id}`).pipe(
      catchError((error) => {
        return throwError(error);
      }),
    );
  }

  getActiveQuotation() {
    return this._activeQuotation.getValue();
  }

  createQuotation(data :FormData) :Observable<any> {
    return this.http.post(`${this.url}/quotations`, data).pipe(
      tap((resp :any) => {
        if (resp.record.parent_id == 0) {
          const quotation = resp.record;
          quotation.tiered_pricing_json = JSON.parse(quotation.tiered_pricing_json);
          quotation.variants_influence_price = JSON.parse(
            quotation.variants_influence_price,
          );
          const record = this._record.getValue();
          if (record) {
            if (record.data && record.data.length >= 0) {
              record.data.splice(0, 0, quotation);
            } else {
              record.data = [quotation];
            }
            this._record.next(record);
          }
        } else {
          const record = this._record.getValue();
          if (record && record.data && record.data.length > 0) {
            const idx = record.data.findIndex((row) => {
              return parseInt(row.id) == parseInt(resp.record.parent_id);
            });
            if (idx || idx >= 0) {
              record.data[idx].variant_quotations_count += 1;
              this._record.next(record);
            }
          }
        }
      }),
    );
  }

  updateQuotation(id :number, data :FormData) :Observable<any> {
    return this.http.post(`${this.url}/quotations/${id}`, data).pipe(
      tap((data :any) => {
        if (data.record.parent_id == 0) {
          const quotation = data.record;
          quotation.tiered_pricing_json = JSON.parse(quotation.tiered_pricing_json);
          quotation.variants_influence_price = JSON.parse(
            quotation.variants_influence_price,
          );
          quotation.files = data.files;
          this.updateRowData(quotation);
        }
      }),
    );
  }

  delete(id :number, parent_id ?:number) {
    return this.http.delete(`${this.url}/quotations/${id}`).pipe(
      tap((data :any) => {
        const record = this._record.getValue();
        if (record && record.data && record.data.length > 0) {
          if (parent_id) {
            const idx = record.data.findIndex((row) => {
              return parseInt(row.id) == parent_id;
            });
            record.data[idx].variant_quotations_count -= 1;
          } else {
            const idx = record.data.findIndex((row) => {
              return row.id == id;
            });
            record.data.splice(idx, 1);
          }
          this._record.next(record);
        }
      }),
    );
  }

  updateRowData(rowData :Quotation) {
    const record = this._record.getValue();
    if (record && record.data && record.data.length > 0) {
      const idx = record.data.findIndex((row) => {
        return row.id == rowData.id;
      });
      if (idx || idx >= 0) {
        const existingQuote = record.data[idx];
        const quote = Object.assign(existingQuote, rowData);
        record.data.splice(idx, 1, quote);
        // record.data.splice(idx, 1, rowData);
        this._record.next(record);
      }
    }
  }

  getNext() {
    this.params.page = this.params.page + 1;
    this.refreshList();
  }

  getPrevious() {
    this.params.page = this.params.page - 1;
    this.refreshList();
  }

  setParams(params :any) {
    this.params = params;
  }

  getQuotationAttachments(quotation_id ?:number) :Observable<any> {
    if (!quotation_id) {
      const quotation :Quotation = this.getActiveQuotation();
      quotation_id = quotation.id;
    }

    return this.http.get(`${this.url}/quotations/${quotation_id}/attachments`);
  }

  getQuotationPriceNegotiations(quotation_id :number) :Observable<any> {
    return this.http
      .get(`${this.url}/quotations/${quotation_id}/negotiations`)
      .pipe(
        catchError((error) => {
          return throwError(error.message);
        }),
      );
  }

  getVariantQuotes(quotation_id :number) :Observable<any> {
    return this.http.get(`${this.url}/quotations/${quotation_id}/variants`).pipe(
      catchError((error) => {
        return throwError(error.message);
      }),
    );
  }

  getQuotationNegotiations(params :any) :Observable<any> {
    return this.http.get(`${this.url}/quotations/negotiations`, {
      params: params,
    });
  }

  getQuotationRequoteRequests(params :any) :Observable<any> {
    return this.http.get(`${this.url}/quotations/requote_requests`, {
      params: params,
    });
  }
}
